/**
 * Records the current browser tab while a tour plays, using the browser's
 * own screen capture (the user is asked which tab to share). No upload
 * happens here — the caller decides what to do with the finished file.
 */

export function canRecordTours() {
  return typeof window !== 'undefined'
    && !!navigator.mediaDevices?.getDisplayMedia
    && typeof window.MediaRecorder !== 'undefined';
}

function pickMimeType() {
  const types = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  return types.find((t) => window.MediaRecorder.isTypeSupported?.(t)) || '';
}

/**
 * Must be called from a click handler. Resolves to a recorder handle once the
 * user has picked what to share, or rejects if they cancel.
 *   handle.stop()     → Promise<Blob>
 *   handle.onEnded(f) → f() runs if the user stops sharing from the browser bar
 */
export async function startTabRecording() {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: 30, displaySurface: 'browser' },
    audio: true,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
  });
  const mimeType = pickMimeType();
  const recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size) chunks.push(e.data);
  };
  recorder.start(1000);

  let endedHandler = null;
  stream.getVideoTracks()[0]?.addEventListener('ended', () => endedHandler && endedHandler());

  return {
    onEnded(fn) {
      endedHandler = fn;
    },
    stop() {
      return new Promise((resolve) => {
        const finish = () => {
          stream.getTracks().forEach((t) => t.stop());
          resolve(new Blob(chunks, { type: 'video/webm' }));
        };
        if (recorder.state === 'inactive') finish();
        else {
          recorder.onstop = finish;
          recorder.stop();
        }
      });
    },
  };
}
